// src/pages/Validate.js
// VALIDATE Page - Dual-mode vulnerability validation
// DriftAlarm.AI - Built by Security Experts

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import tierService from '../services/tierService';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import {
  validateService,
  DEFAULT_VALIDATION_TYPES,
  VALIDATION_TYPE_INFO,
  calculateTargetCounts
} from '../services/validateService';
import { scanService } from '../services/api';
import './Validate.css';

// Group validation types by category
const VALIDATION_GROUPS = [
  {
    name: 'Web Application Tests',
    icon: '🌐',
    types: ['xss', 'sqli', 'cors', 'openRedirect', 'securityHeaders', 'directoryListing', 'sensitiveFiles']
  },
  {
    name: 'Authentication Tests',
    icon: '🔐',
    types: ['defaultCreds']
  },
  {
    name: 'Service Tests',
    icon: '🖥️',
    types: ['anonymousFtp', 'anonymousMongo', 'anonymousRedis', 'anonymousElastic', 'smtpRelay']
  },
  {
    name: 'Infrastructure Tests',
    icon: '🏗️',
    types: ['sslTls', 'subdomainTakeover']
  }
];

function Validate() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { instance, accounts } = useMsal();

  // Core state
  const [userTier, setUserTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mode state
  const [mode, setMode] = useState('standalone');
  const [target, setTarget] = useState('');

  // Source state (for integrated mode)
  const [sourceType, setSourceType] = useState(null);
  const [sourceScanId, setSourceScanId] = useState(null);
  const [sourceAssetId, setSourceAssetId] = useState(null);
  const [inheritedData, setInheritedData] = useState(null);

  // Validation configuration
  const [validationTypes, setValidationTypes] = useState({ ...DEFAULT_VALIDATION_TYPES });
  const [targetCounts, setTargetCounts] = useState({});

  // Validation job state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [validating, setValidating] = useState(false);
  const [results, setResults] = useState(null);

  // Recent scans/assets for quick launch
  const [recentScans, setRecentScans] = useState([]);
  const [recentAssets, setRecentAssets] = useState([]);

  // Countdown timer for auto-redirect to full results
  const [countdown, setCountdown] = useState(10);
  const countdownRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

  const getToken = useCallback(async () => {
    if (!accounts[0]) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      });
      return response.idToken;
    } catch (err) {
      console.error('Token acquisition failed:', err);
      return null;
    }
  }, [instance, accounts]);

  // Load user profile and check tier
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const tierInfo = await tierService.getCachedTierInfo();
        setUserTier(tierInfo?.tier || 'free');
      } catch (err) {
        console.error('Failed to load tier info:', err);
      } finally {
        setLoading(false);
      }
    };
    loadUserProfile();
  }, []);

  // Load SCRUTINIZE data
  const loadScrutinizeData = useCallback(async (scanId) => {
    try {
      const result = await scanService.getScrutinizeResult(scanId);
      if (result.success && result.scanData) {
        const data = validateService.extractScrutinizeData(result.scanData);
        setInheritedData(data);
        setTarget(data.target || '');
        setTargetCounts(calculateTargetCounts(data));
      }
    } catch (err) {
      console.error('Failed to load SCRUTINIZE data:', err);
      setError('Failed to load scan data');
    }
  }, []);

  // Load Asset data
  const loadAssetData = useCallback(async (assetId) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/asset-scan-details/${assetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        const data = validateService.extractAssetData(result);
        setInheritedData(data);
        setTarget(data.target || '');
        setTargetCounts(calculateTargetCounts(data));
      }
    } catch (err) {
      console.error('Failed to load asset data:', err);
      setError('Failed to load asset data');
    }
  }, [getToken, API_BASE]);

  // Handle integrated mode from URL params
  useEffect(() => {
    const scanId = searchParams.get('scanId');
    const assetId = searchParams.get('assetId');
    const targetParam = searchParams.get('target');

    if (scanId) {
      setMode('integrated');
      setSourceType('scrutinize');
      setSourceScanId(scanId);
      if (targetParam) setTarget(targetParam);
      loadScrutinizeData(scanId);
    } else if (assetId) {
      setMode('integrated');
      setSourceType('asset');
      setSourceAssetId(assetId);
      if (targetParam) setTarget(targetParam);
      loadAssetData(assetId);
    } else if (location.state?.inheritedData) {
      setMode('integrated');
      setInheritedData(location.state.inheritedData);
      // Target is at top level of state, not inside inheritedData
      setTarget(location.state.target || location.state.inheritedData.target || '');
      setSourceType(location.state.sourceType);
      setSourceScanId(location.state.sourceScanId);
      setSourceAssetId(location.state.sourceAssetId);
      setTargetCounts(calculateTargetCounts(location.state.inheritedData));
    } else if (location.state?.mode === 'integrated') {
      // Handle case where mode is integrated but inheritedData not provided
      // Don't load data here - let the API fetch from blob storage for better reliability
      setMode('integrated');
      setTarget(location.state.target || '');
      setSourceType(location.state.sourceType);
      setSourceScanId(location.state.sourceScanId);
      setSourceAssetId(location.state.sourceAssetId);
      // Note: inheritedData stays null - API will fetch from source scan blob
    }
  }, [searchParams, location.state, loadScrutinizeData, loadAssetData]);

  // Load recent scans and assets
  useEffect(() => {
    const loadRecents = async () => {
      try {
        const token = await getToken();
        if (!token) return;

        const scansRes = await fetch(`${API_BASE}/scan-list?type=scrutinize&limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (scansRes.ok) {
          const data = await scansRes.json();
          setRecentScans(data.scans?.filter(s => s.status === 'complete') || []);
        }

        const assetsRes = await fetch(`${API_BASE}/asset-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (assetsRes.ok) {
          const data = await assetsRes.json();
          setRecentAssets(data.assets?.filter(a => a.status !== 'deleted') || []);
        }
      } catch (err) {
        console.error('Failed to load recents:', err);
      }
    };

    if (userTier === 'pro' || userTier === 'enterprise') {
      loadRecents();
    }
  }, [userTier, getToken, API_BASE]);

  // Countdown timer for auto-redirect to full results
  useEffect(() => {
    if (results && jobId) {
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            navigate(`/validate/results/${jobId}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
      };
    }
  }, [results, jobId, navigate]);

  // Handle validation type toggle
  const handleTypeToggle = (typeKey) => {
    setValidationTypes(prev => ({ ...prev, [typeKey]: !prev[typeKey] }));
  };

  // Select/deselect all
  const handleSelectAll = (select) => {
    const updated = {};
    Object.keys(DEFAULT_VALIDATION_TYPES).forEach(key => { updated[key] = select; });
    setValidationTypes(updated);
  };

  // Start validation
  const handleStartValidation = async () => {
    if (!target.trim() && mode === 'standalone') {
      setError('Please enter a target domain or IP');
      return;
    }

    setError(null);
    setValidating(true);
    setJobStatus('starting');

    try {
      const response = await validateService.startValidation({
        target: target.trim(),
        mode,
        sourceType,
        sourceScanId,
        sourceAssetId,
        validationTypes,
        inheritedData
      });

      if (response.success) {
        setJobId(response.jobId);
        setJobStatus('pending');
        pollForResults(response.jobId);
      } else {
        throw new Error(response.error || 'Failed to start validation');
      }
    } catch (err) {
      console.error('Validation error:', err);
      setError(err.message);
      setValidating(false);
      setJobStatus(null);
    }
  };

  // Poll for results
  const pollForResults = async (jId) => {
    try {
      const result = await validateService.pollValidateCompletion(
        jId,
        (status) => setJobStatus(status.status),
        1800000
      );
      setResults(result);
      setValidating(false);
      setJobStatus('complete');
    } catch (err) {
      console.error('Polling error:', err);
      setError(err.message);
      setValidating(false);
      setJobStatus('failed');
    }
  };

  // Quick launch handlers
  const handleQuickLaunchScan = (scan) => {
    setMode('integrated');
    setSourceType('scrutinize');
    setSourceScanId(scan.scanId);
    setTarget(scan.target);
    loadScrutinizeData(scan.scanId);
  };

  const handleQuickLaunchAsset = (asset) => {
    setMode('integrated');
    setSourceType('asset');
    setSourceAssetId(asset.id);
    setTarget(asset.assetValue);
    loadAssetData(asset.id);
  };

  if (loading) {
    return (
      <div className="validate-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (validating) {
    return (
      <div className="validate-page">
        <div className="validation-progress-container">
          <div className="progress-icon">⚡</div>
          <h2>Validation in Progress</h2>
          <p className="progress-target">Target: {target}</p>
          <div className="progress-status">
            <div className="status-indicator">
              <span className={`status-dot ${jobStatus}`}></span>
              <span className="status-text">
                {jobStatus === 'pending' && 'Initializing...'}
                {jobStatus === 'discovery' && 'Running mini-discovery...'}
                {jobStatus === 'validation' && 'Running validation modules...'}
                {jobStatus === 'processing' && 'Processing results...'}
              </span>
            </div>
          </div>
          <div className="progress-bar"><div className="progress-fill" style={{ width: jobStatus === 'validation' ? '60%' : '30%' }}></div></div>
          <p className="progress-note">{mode === 'standalone' ? 'Estimated time: 5-8 minutes' : 'Estimated time: 3-5 minutes'}</p>
        </div>
      </div>
    );
  }

  if (results) {
    return (
      <div className="validate-page">
        <div className="validate-header">
          <h1><span className="validate-icon">✓</span> VALIDATE Results</h1>
          <p className="subtitle">Target: {target}</p>
        </div>
        <div className="validation-results-summary">
          <div className="summary-cards">
            <div className="summary-card confirmed"><div className="card-value">{results.summary?.confirmed || 0}</div><div className="card-label">Confirmed</div></div>
            <div className="summary-card not-exploitable"><div className="card-value">{results.summary?.notExploitable || 0}</div><div className="card-label">Not Exploitable</div></div>
            <div className="summary-card inconclusive"><div className="card-value">{results.summary?.inconclusive || 0}</div><div className="card-label">Inconclusive</div></div>
          </div>
          {results.aiCommentary && (
            <div className="ai-commentary">
              <div className="commentary-header">
                <span className="ai-badge r3dteam-brief">DriftAlarm BRIEF</span>
                <span className={`risk-badge risk-${results.aiCommentary.riskAssessment?.toLowerCase()}`}>{results.aiCommentary.riskAssessment} Risk</span>
              </div>
              <p className="executive-summary">{results.aiCommentary.executiveSummary}</p>
            </div>
          )}
          <div className="view-results-section">
            <button className="view-details-btn large centered" onClick={() => navigate(`/validate/results/${jobId}`)}>View Full Results →</button>
            <div className="countdown-timer">
              <span className="countdown-text">Redirecting in</span>
              <span className="countdown-number">{countdown}</span>
              <span className="countdown-text">seconds</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CleanModernLayout>
    <div className="validate-page">
      <div className="validate-header">
        <h1><span className="validate-icon">✓</span> VALIDATE</h1>
        <p className="subtitle">Vulnerability Validation & False Positive Elimination</p>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span> {error}
          <button className="dismiss-btn" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="validate-content validate-layout">
        <div className="validate-main">
          <div className="mode-selection">
            <h3>Validation Mode</h3>
            <div className="mode-options">
              <label className={`mode-option ${mode === 'standalone' ? 'active' : ''}`}>
                <input type="radio" name="mode" value="standalone" checked={mode === 'standalone'}
                  onChange={() => { setMode('standalone'); setInheritedData(null); setSourceType(null); setTargetCounts({}); }} />
                <div className="mode-content">
                  <span className="mode-icon">🎯</span>
                  <div><strong>Standalone</strong><p>Enter target, run mini-discovery, then validate</p></div>
                </div>
              </label>
              <label className={`mode-option ${mode === 'integrated' ? 'active' : ''}`}>
                <input type="radio" name="mode" value="integrated" checked={mode === 'integrated'} onChange={() => setMode('integrated')} />
                <div className="mode-content">
                  <span className="mode-icon">🔗</span>
                  <div><strong>Integrated</strong><p>Use data from existing Deep Scan or Asset scan</p></div>
                </div>
              </label>
            </div>
          </div>

          <div className="target-section">
            <h3>Target</h3>
            {mode === 'standalone' ? (
              <div className="target-input-group">
                <input type="text" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Enter domain or IP (e.g., example.com)" className="target-input" />
              </div>
            ) : (
              <div className="integrated-target">
                {(sourceScanId || sourceAssetId) ? (
                  <div className="inherited-summary">
                    <div className="target-display">{target}</div>
                    {inheritedData ? (
                      <div className="inherited-stats">
                        <span>📡 {inheritedData.subdomains?.length || 0} subdomains</span>
                        <span>🌐 {inheritedData.liveUrls?.length || 0} live URLs</span>
                        <span>🚪 {inheritedData.openPorts?.length || 0} open ports</span>
                      </div>
                    ) : (
                      <div className="inherited-stats">
                        <span>Discovery data will be loaded from source scan</span>
                      </div>
                    )}
                    <div className="source-badge">Source: {sourceType === 'scrutinize' ? 'Deep Scan' : 'Asset Baseline'}</div>
                  </div>
                ) : (
                  <div className="select-source">
                    <p>Select a recent scan or asset to validate:</p>
                    {recentScans.length > 0 && (
                      <div className="source-list">
                        <h4>Recent Deep Scans</h4>
                        {recentScans.map(scan => (
                          <button key={scan.scanId} className="source-item" onClick={() => handleQuickLaunchScan(scan)}>
                            <span className="source-icon">📊</span>
                            <span className="source-target">{scan.target}</span>
                            <span className="source-date">{new Date(scan.completedAt).toLocaleDateString()}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {recentAssets.length > 0 && (
                      <div className="source-list">
                        <h4>Monitored Assets</h4>
                        {recentAssets.map(asset => (
                          <button key={asset.id} className="source-item" onClick={() => handleQuickLaunchAsset(asset)}>
                            <span className="source-icon">🏢</span>
                            <span className="source-target">{asset.assetValue}</span>
                            <span className="source-date">{asset.lastScanAt ? new Date(asset.lastScanAt).toLocaleDateString() : 'No scan'}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="validation-config">
            <div className="config-header">
              <h3>Validation Types</h3>
              <div className="bulk-actions">
                <button onClick={() => handleSelectAll(true)}>Select All</button>
                <button onClick={() => handleSelectAll(false)}>Deselect All</button>
              </div>
            </div>
            {VALIDATION_GROUPS.map(group => (
              <div key={group.name} className="validation-group">
                <h4>{group.icon} {group.name}</h4>
                <div className="validation-checkboxes">
                  {group.types.map(typeKey => {
                    const info = VALIDATION_TYPE_INFO[typeKey];
                    const count = targetCounts[typeKey] || 0;
                    const disabled = mode === 'integrated' && count === 0;
                    return (
                      <label key={typeKey} className={`validation-type ${disabled ? 'disabled' : ''}`}>
                        <input type="checkbox" checked={validationTypes[typeKey]} disabled={disabled} onChange={() => handleTypeToggle(typeKey)} />
                        <span className="type-label">{info?.label || typeKey}</span>
                        {mode === 'integrated' && <span className="target-count">({count} targets)</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="start-section">
            <button
              className="start-validation-btn"
              onClick={handleStartValidation}
              disabled={!target.trim() || (mode === 'integrated' && !sourceScanId && !sourceAssetId)}
            >
              <span className="btn-icon">⚡</span> Start Validation
            </button>
            <p className="time-estimate">{mode === 'standalone' ? 'Estimated time: 5-8 minutes' : 'Estimated time: 3-5 minutes'}</p>
          </div>
        </div>

        <div className="validate-sidebar">
          <div className="info-card">
            <h3>About VALIDATE</h3>
            <p>VALIDATE confirms vulnerabilities through safe, controlled testing. It eliminates false positives and provides evidence for confirmed findings.</p>
          </div>
          <div className="info-card">
            <h3>Safety First</h3>
            <ul><li>Non-destructive testing only</li><li>No actual exploitation</li><li>Rate-limited requests</li><li>No data modification</li></ul>
          </div>
          <div className="tier-card">
            <span className={`tier-badge ${userTier}`}>{userTier.toUpperCase()}</span>
            <p>50 validations per week</p>
          </div>
        </div>
      </div>
    </div>
    </CleanModernLayout>
  );
}

export default Validate;
